import React, { useState, useEffect } from 'react';
import { useComponentTranslations } from '../hooks/useTranslations';
import { Cookie } from 'lucide-react';

const CookieConsentBanner = () => {
  const { t, loading } = useComponentTranslations('cookies');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('dreamlineOperationsCookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  if (loading || !showBanner) {
    return null;
  }

  const handleAcceptAll = () => {
    localStorage.setItem('dreamlineOperationsCookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleOnlyEssentials = () => {
    localStorage.setItem('dreamlineOperationsCookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleDenyAll = () => {
    localStorage.setItem('dreamlineOperationsCookieConsent', JSON.stringify({
      necessary: true, // Necessary cookies can't be denied
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-800 text-white p-6 shadow-lg border-t-2 border-cyan-500">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side - Icon and Message */}
          <div className="flex items-center space-x-4 flex-1">
            <div className="flex-shrink-0">
              <Cookie className="w-8 h-8 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">
                🍪 {t('title', 'We use cookies to enhance your experience')}
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                {t('message', 'We use cookies to provide personalized content, analyze site traffic, and enhance your browsing experience. By clicking "Accept All", you agree to our use of cookies as described in our Privacy Policy.')}
              </p>
            </div>
          </div>

          {/* Right side - Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleOnlyEssentials}
              className="px-6 py-2 bg-transparent border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-white transition-all duration-300 rounded-lg font-medium"
            >
              {t('only_essentials', 'Only Essentials')}
            </button>
            
            <button
              onClick={handleDenyAll}
              className="px-6 py-2 bg-transparent border-2 border-gray-500 text-gray-400 hover:bg-gray-500 hover:text-white transition-all duration-300 rounded-lg font-medium"
            >
              {t('deny_all', 'Deny All')}
            </button>
            
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 bg-gradient-to-r from-dream-teal to-evergreen text-white hover:from-dream-teal-600 hover:to-evergreen-700 transition-all duration-300 rounded-lg font-medium shadow-lg"
            >
              {t('accept_all', 'Accept All')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;